import React from 'react';
import { Dimensions, StyleSheet } from 'react-native';
import Animated, {
  useAnimatedGestureHandler,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import {
  PanGestureHandler,
  PinchGestureHandler,
} from 'react-native-gesture-handler';

const { width, height } = Dimensions.get('window');

const styles = StyleSheet.create({
  image: {
    backgroundColor: 'transparent',
    width: width * 0.9,
    height: height * 0.7,
    resizeMode: 'contain',
    elevation: 0,
  },
});

export default function ZoomableImage({ source, style }) {
  const scale = useSharedValue(1);
  const translateX = useSharedValue(0);
  const translateY = useSharedValue(0);

  const panGestureHandler = useAnimatedGestureHandler({
    onStart: (event, context) => {
      context.translateX = translateX.value;
      context.translateY = translateY.value;
    },
    onActive: (event, context) => {
      translateX.value = context.translateX + event.translationX;
      translateY.value = context.translateY + event.translationY;
    },
    onEnd: () => {
      const imageWidth = width * 0.9 * scale.value;
      const maxTranslateX = imageWidth > width ? (imageWidth - width) / 2 : 0;

      if (translateX.value > maxTranslateX) {
        translateX.value = withTiming(maxTranslateX);
      } else if (translateX.value < -maxTranslateX) {
        translateX.value = withTiming(-maxTranslateX);
      }

      const imageHeight = height * 0.7 * scale.value;
      const maxTranslateY = imageHeight > height * 0.7 ? (imageHeight - height * 0.7) / 2 : 0;

      if (translateY.value > maxTranslateY) {
        translateY.value = withTiming(maxTranslateY);
      } else if (translateY.value < -maxTranslateY) {
        translateY.value = withTiming(-maxTranslateY);
      }
    },
  });

  const pinchGestureHandler = useAnimatedGestureHandler({
    onStart: (event, context) => {
      context.scale = scale.value;
      context.translateX = translateX.value;
      context.translateY = translateY.value;
      context.focalX = event.focalX;
      context.focalY = event.focalY;
    },
    onActive: (event, context) => {
      const newScale = context.scale * event.scale;
      if (newScale < 1) {
        scale.value = 1;
        translateX.value = 0;
        translateY.value = 0;
      } else {
        scale.value = newScale;
        const { focalX } = event;
        const { focalY } = event;
        const deltaX = focalX - context.focalX;
        const deltaY = focalY - context.focalY;
        translateX.value = context.translateX
          + deltaX
          + (context.translateX - focalX) * (event.scale - 1);
        translateY.value = context.translateY
          + deltaY
          + (context.translateY - focalY) * (event.scale - 1);
      }
    },
    onEnd: () => {
      if (scale.value < 1) {
        scale.value = withTiming(1);
        translateX.value = withTiming(0);
        translateY.value = withTiming(0);
      }
    },
  });

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [
      { translateX: translateX.value },
      { translateY: translateY.value },
      { scale: scale.value },
    ],
  }));

  const pinchRef = React.useRef();
  const panRef = React.useRef();

  return (
    <PanGestureHandler
      ref={panRef}
      onGestureEvent={panGestureHandler}
      simultaneousHandlers={pinchRef}
    >
      <Animated.View
        style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}
      >
        <PinchGestureHandler
          ref={pinchRef}
          onGestureEvent={pinchGestureHandler}
          simultaneousHandlers={panRef}
        >
          <Animated.Image
            accessibilityLabel="zoomable-image"
            style={[styles.image, style, animatedStyle]}
            source={source}
          />
        </PinchGestureHandler>
      </Animated.View>
    </PanGestureHandler>
  );
}
